import { SearchResultCard } from "@components/job_search/search_result_card.tsx";
import useDevice from "@hooks/use_device.ts";
import { Deferred, WhenVisible } from "@inertiajs/react";
import useTranslation from "@hooks/use_translation.ts";
import { type ReloadOptions } from "@inertiajs/core";
import { useMemo, useState } from "react";
import Fingerprint1 from "@images/fingerprint_1.svg";
import EmptyState from "@components/empty_state.tsx";
import type { JobPost, SearchParams } from "@/pages/job_search";
import { Body } from "@sprout/typography.tsx";
import { JobSearchQuickApply } from "@components/job_search/job_search_quick_apply.tsx";
import { ExpandSearchCard } from "./expand_search_card";

const MIN_JOB_POSTS_TO_SHOW_QUICK_APPLY_CARD = 3;
const SKELETON_COUNT = 12;

type Props = {
  jobPosts: JobPost[] | undefined;
  browsing: boolean;
  moreResultsAvailable: boolean;
  currentPage: number;
  lastPublishDate: string | null;
  emptyStateMessage: string;
  searchParams: SearchParams;
  createTrackSearchResultClick: (postId: string, title: string) => () => void;
  onView: (id: string) => void;
};

type SearchResultListProps = {
  jobPosts: JobPost[];
  moreResultsAvailable: boolean;
  createTrackSearchResultClick: (postId: string, title: string) => () => void;
  onView: (id: string) => void;
};

const SearchResultList = ({
  jobPosts,
  moreResultsAvailable,
  createTrackSearchResultClick,
  onView,
}: SearchResultListProps) => {
  const resultCards = useMemo(() => {
    const jobPostCards = jobPosts.map((post) => (
      <SearchResultCard
        key={post.id}
        id={post.id}
        title={post.title}
        company={post.companyName}
        logoUrl={post.logoUrl}
        publishedAt={post.firstPublished}
        url={post.publicUrl}
        locations={post.locations}
        workType={post.workType}
        payRange={post.payRanges}
        trackClick={createTrackSearchResultClick(post.id, post.title)}
        viewed={post.viewed}
        onView={onView}
      />
    ));
    // JobSearchQuickApply card is only shown when the following conditions are met:
    // There are at least MIN_JOB_POSTS_TO_SHOW_QUICK_APPLY_CARD job posts
    // The user has not completed their quick apply profile
    if (jobPostCards.length >= MIN_JOB_POSTS_TO_SHOW_QUICK_APPLY_CARD) {
      jobPostCards.splice(
        MIN_JOB_POSTS_TO_SHOW_QUICK_APPLY_CARD,
        0,
        <JobSearchQuickApply key="job-search-quick-apply" />,
      );
    }

    if (jobPosts.length > 0 && !moreResultsAvailable) {
      jobPostCards.push(<ExpandSearchCard key="expand-search-card" />);
    }

    return jobPostCards;
  }, [jobPosts, createTrackSearchResultClick, onView]);

  return <>{resultCards}</>;
};

const LoadingResults = () => {
  return (
    <>
      {[...Array(SKELETON_COUNT)].map((_, i) => (
        <SearchResultCard
          key={i}
          id={i.toString()}
          title={""}
          company={""}
          logoUrl={""}
          publishedAt={""}
          url={""}
          loading={true}
          viewed={false}
          onView={() => {}}
        />
      ))}
    </>
  );
};

const JobSearchTable = ({
  jobPosts,
  browsing,
  moreResultsAvailable,
  currentPage,
  lastPublishDate,
  emptyStateMessage,
  createTrackSearchResultClick,
  searchParams,
  onView,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("jobSearch");
  const device = useDevice();

  const reloadParams: ReloadOptions = {
    data: {
      ...searchParams,
      ...(!browsing && { page: currentPage + 1 }), // The page number is only relevant when we're not browsing
      ...(browsing && { search_after: lastPublishDate }), // When browsing, we use a "live cursor" instead of page number
    },
    preserveUrl: true,
    only: ["jobPosts", "moreResultsAvailable", "page"],
    onBefore: () => {
      setLoading(true);
    },
    onFinish: () => {
      setLoading(false);
    },
  };

  return (
    <>
      {jobPosts?.length === 0 ? (
        <EmptyState
          svg={Fingerprint1}
          svgSize={"150"}
          fontWeight={"400"}
          message={emptyStateMessage}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Deferred fallback={<LoadingResults />} data={"jobPosts"}>
              <SearchResultList
                jobPosts={jobPosts!}
                moreResultsAvailable={moreResultsAvailable}
                createTrackSearchResultClick={createTrackSearchResultClick}
                onView={onView}
              />
            </Deferred>
            {loading && <LoadingResults />}
          </div>
          <div className="flex flex-col gap-3 justify-center items-center">
            {device.isWeb && <Body light>{t("aiDisclaimer")}</Body>}
            {moreResultsAvailable && (
              <WhenVisible fallback={t("loading")} params={reloadParams} always>
                <div></div>
              </WhenVisible>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default JobSearchTable;
